import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, ChevronLeft, Loader2, FileText, RefreshCw, ExternalLink, Eye, Edit, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';

interface WizardBlogProps {
  onNext: () => void;
  onPrevious: () => void;
  siteId?: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  category: string;
  excerpt: string | null;
  is_published: boolean;
  main_image_url: string | null;
  created_at: string;
}

interface GenerationJob {
  id: string;
  status: string;
  progress: number;
  total_items: number;
  error_message: string | null;
}

export function WizardBlog({ onNext, onPrevious, siteId }: WizardBlogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [job, setJob] = useState<GenerationJob | null>(null);

  useEffect(() => {
    if (siteId) {
      fetchPosts();
      
      const channel = supabase
        .channel(`blog-job-${siteId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'generation_jobs',
            filter: `site_id=eq.${siteId}`,
          },
          () => fetchPosts()
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'blog_posts',
            filter: `site_id=eq.${siteId}`,
          },
          () => fetchPosts()
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [siteId]);

  const fetchPosts = async () => {
    if (!siteId) return;

    try {
      const [postsRes, jobRes] = await Promise.all([
        supabase
          .from('blog_posts')
          .select('id, title, slug, category, excerpt, is_published, main_image_url, created_at')
          .eq('site_id', siteId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('generation_jobs')
          .select('id, status, progress, total_items, error_message')
          .eq('site_id', siteId)
          .eq('job_type', 'blog_posts')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      setPosts(postsRes.data || []);
      setJob(jobRes.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMorePosts = async () => {
    if (!siteId) return;

    setGenerating(true);
    try {
      // Create a new job for blog post generation
      await supabase
        .from('generation_jobs')
        .insert({ 
          site_id: siteId, 
          job_type: 'blog_posts', 
          status: 'pending',
          total_items: 3
        });

      const session = await supabase.auth.getSession();
      if (session.data.session) {
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-generation-jobs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.data.session.access_token}`,
          },
          body: JSON.stringify({}),
        }).catch(() => {});
      }

      toast({ title: 'Blog post generation started!' });
      fetchPosts();
    } catch (error: any) {
      toast({
        title: 'Error starting generation',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const retryGeneration = async () => {
    if (!siteId) return;

    try {
      await supabase
        .from('generation_jobs')
        .delete()
        .eq('site_id', siteId)
        .eq('job_type', 'blog_posts')
        .eq('status', 'failed');

      await generateMorePosts();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isProcessing = job?.status === 'processing' || job?.status === 'pending';
  const isFailed = job?.status === 'failed';
  const hasPosts = posts.length > 0;
  const progressPercent = job && job.total_items > 0 
    ? Math.round((job.progress / job.total_items) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <CardTitle>Blog Posts</CardTitle>
            </div>
            {hasPosts && <Badge variant="default">{posts.length} posts</Badge>}
            {isProcessing && <Badge variant="secondary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating</Badge>}
            {isFailed && <Badge variant="destructive">Failed</Badge>}
          </div>
          <CardDescription>
            Review and edit AI-generated blog posts for your site.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progressPercent} className="h-2" />
              <p className="text-sm text-muted-foreground text-center">
                AI is generating blog posts ({job?.progress || 0} of {job?.total_items || 0})...
              </p>
            </div>
          )}

          {isFailed && (
            <div className="text-center space-y-3">
              <p className="text-sm text-destructive">{job?.error_message || 'Generation failed'}</p>
              <Button variant="outline" onClick={retryGeneration}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry Generation
              </Button>
            </div>
          )}

          {hasPosts ? (
            <>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-3">
                  {posts.map((post) => (
                    <div 
                      key={post.id}
                      className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      {post.main_image_url && (
                        <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                          <img 
                            src={post.main_image_url} 
                            alt={post.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-xs">{post.category}</Badge>
                          {post.is_published && (
                            <Badge variant="default" className="text-xs">Published</Badge>
                          )}
                        </div>
                        <h4 className="font-medium text-sm line-clamp-1">{post.title}</h4>
                        {post.excerpt && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                            {post.excerpt}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => window.open(`/admin/blog/${post.id}/preview`, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => window.open(`/admin/blog/${post.id}/edit`, '_blank')}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={() => window.open('/admin/blog', '_blank')}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Manage All Posts
                </Button>
                <Button variant="outline" onClick={generateMorePosts} disabled={generating || isProcessing}>
                  {generating || isProcessing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Generate More
                </Button>
              </div>
            </>
          ) : !isProcessing && !isFailed && (
            <div className="text-center space-y-4">
              <p className="text-sm text-muted-foreground">
                No blog posts have been created yet. Generate posts to engage visitors with local content.
              </p>
              <Button onClick={generateMorePosts} disabled={generating}>
                {generating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Generate Blog Posts
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <Button onClick={onNext} disabled={isProcessing}>
          {isProcessing ? 'Please wait...' : 'Continue to Publish'}
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
